import { useEffect, useRef } from 'react';
import { ServiceData } from '../../../mocks/servicesData';
import { PHONE_1 } from '../../../components/feature/TopBar';

interface Props {
  service: ServiceData;
  index: number;
}

const WA_BASE = `https://wa.me/90${PHONE_1}?text=`;

const serviceImages: Record<string, string> = {
  koltuk:
    'https://readdy.ai/api/search-image?query=professional%20sofa%20cleaning%20technician%20using%20extraction%20machine%20on%20light%20grey%20fabric%20couch%20in%20bright%20living%20room%20clean%20minimal%20background&width=600&height=450&seq=svc01&orientation=landscape',
  sandalye:
    'https://readdy.ai/api/search-image?query=professional%20chair%20upholstery%20cleaning%20with%20steam%20machine%20on%20dining%20chair%20fabric%20bright%20clean%20minimal%20studio%20setting&width=600&height=450&seq=svc02&orientation=landscape',
  yatak:
    'https://readdy.ai/api/search-image?query=professional%20mattress%20deep%20cleaning%20service%20using%20extraction%20vacuum%20machine%20on%20white%20mattress%20hotel%20room%20setting%20bright&width=600&height=450&seq=svc03&orientation=landscape',
  petek:
    'https://readdy.ai/api/search-image?query=professional%20radiator%20cleaning%20service%20technician%20flushing%20heating%20system%20with%20machine%20in%20apartment%20white%20walls%20clean&width=600&height=450&seq=svc04&orientation=landscape',
};

export default function ServiceDetailSection({ service, index }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isEven = index % 2 === 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const waLink = `${WA_BASE}${encodeURIComponent(service.whatsappMsg)}`;

  return (
    <section
      id={`service-detail-${service.id}`}
      className={`w-full py-16 md:py-24 ${isEven ? 'bg-[#f8faf9]' : 'bg-white'}`}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div
          ref={ref}
          className="opacity-0 translate-y-8 transition-all duration-700"
        >
          {/* Section header */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-xl">
              <i className={`${service.icon} text-emerald-600 text-2xl`} />
            </div>
            <div>
              <span className="text-emerald-600 font-semibold text-xs uppercase tracking-widest">Detay</span>
              <h2 className="text-2xl md:text-4xl font-extrabold text-[#1a1a1a]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {service.title}
              </h2>
            </div>
          </div>

          {/* A. Problem + Image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-center">
            <div className={`${isEven ? '' : 'lg:order-2'}`}>
              <div className="bg-red-50 border-l-4 border-red-400 rounded-r-2xl p-6 mb-5">
                <h3 className="font-bold text-xl text-[#1a1a1a] mb-2">{service.problem.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.problem.desc}</p>
              </div>

              {/* Trust Note */}
              {service.trustNote && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex gap-3 mb-5">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-amber-500 text-lg" />
                  </span>
                  <p className="text-amber-800 text-sm font-medium">{service.trustNote}</p>
                </div>
              )}
            </div>

            <div className={`${isEven ? '' : 'lg:order-1'}`}>
              <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden">
                <img
                  src={serviceImages[service.id]}
                  alt={service.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* B. Common Mistakes */}
          <div className="mb-12">
            <h3 className="font-bold text-xl text-[#1a1a1a] mb-5 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-500" />
              </span>
              Sık Yapılan Hatalar
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {service.mistakes.map((m) => (
                <div key={m.title} className="bg-white border border-red-100 rounded-2xl p-5 hover:-translate-y-1 transition-transform">
                  <h4 className="font-bold text-[#1a1a1a] text-sm mb-2 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line text-red-500" />
                    </span>
                    {m.title}
                  </h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* C. Why it becomes worse */}
          <div className="mb-12 bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-4">
            <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5 bg-amber-100 rounded-xl">
              <i className="ri-error-warning-line text-amber-600 text-xl" />
            </span>
            <div>
              <h3 className="font-bold text-[#1a1a1a] mb-2 text-lg">Neden Daha da Kötüleşir?</h3>
              <p className="text-gray-700 leading-relaxed">{service.whyWorse}</p>
            </div>
          </div>

          {/* D. Correct Method */}
          <div className="mb-12">
            <h3 className="font-bold text-xl text-[#1a1a1a] mb-6 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-500" />
              </span>
              Doğru Yöntem: Adım Adım
            </h3>
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-emerald-100 hidden md:block" />
              <div className="flex flex-col gap-4">
                {service.correctMethod.map((step, i) => (
                  <div key={step.step} className="flex gap-4 items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-500 text-white font-bold rounded-full flex-shrink-0 z-10 text-sm">
                      {i + 1}
                    </div>
                    <div className="bg-white border border-gray-100 rounded-xl p-4 flex-1">
                      <h4 className="font-bold text-[#1a1a1a] text-sm mb-1">{step.step}</h4>
                      <p className="text-gray-500 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* E. Advantages */}
          <div className="mb-12">
            <h3 className="font-bold text-xl text-[#1a1a1a] mb-5 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shield-check-line text-emerald-500" />
              </span>
              Neden Bizi Seçmelisiniz?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {service.advantages.map((adv) => (
                <div key={adv} className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-emerald-600 text-base" />
                  </span>
                  <span className="text-gray-700 text-sm font-medium">{adv}</span>
                </div>
              ))}
            </div>
          </div>

          {/* F. CTA */}
          <div className="bg-[#1a1a1a] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white font-bold text-xl mb-1">{service.title} İçin Hemen Başlayalım</h3>
              <p className="text-gray-400 text-sm">WhatsApp üzerinden hızlıca randevu alın, ekibimiz size ulaşsın.</p>
            </div>
            <a
              href={waLink}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-base px-8 py-4 rounded-xl transition-all hover:scale-105 shadow-lg shadow-emerald-500/30 whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </span>
              {service.ctaLabel}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
